import { useEffect, useState } from "react";
import { toast } from "sonner";
import { House } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";
import { formatET } from "../lib/dateUtils";

const STAGES = ["Applied", "Under Review", "Approved", "Funded", "Declined"];
const navItems = [{ to: "/admin/funding", label: "Funding Pipeline", icon: <House size={18} weight="duotone" />, testId: "nav-admin-funding" }];

export default function AdminFundingPage() {
  const confirmEdit = useEditConfirm();
  const [records, setRecords] = useState([]);
  const [notesDraft, setNotesDraft] = useState({});
  const [amountDraft, setAmountDraft] = useState({});

  const fetchAll = async () => {
    const { data } = await api.get("/admin/funding");
    setRecords(data);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const updateStage = async (id, stage) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.store_display_name || "this application"}'s stage`))) return;
    try {
      await api.patch(`/admin/funding/${id}`, { stage });
      toast.success("Stage updated");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveAmount = async (id, currentAmount) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.store_display_name || "this application"}'s funded amount`))) return;
    try {
      const value = amountDraft[id] !== undefined ? parseFloat(amountDraft[id]) : currentAmount;
      await api.patch(`/admin/funding/${id}`, { amount_funded: value });
      toast.success("Funded amount saved");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveNotes = async (id, currentNotes) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.store_display_name || "this application"}'s notes`))) return;
    try {
      const value = notesDraft[id] !== undefined ? notesDraft[id] : currentNotes;
      await api.patch(`/admin/funding/${id}`, { admin_notes: value });
      toast.success("Notes saved");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Business Funding" moduleBackPath="/admin">
      <div data-testid="admin-funding-page">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-8" title="Every business funding application in the system, with its current pipeline stage and funded amount.">Business Funding Pipeline</h1>
        {records.length === 0 && <p className="text-igc-muted text-sm">No funding applications yet.</p>}
        <div className="space-y-4">
          {records.map((r) => (
            <div key={r.id} className="bg-igc-surface border border-igc-gold/15 p-6" data-testid={`funding-record-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-serif text-lg text-igc-cream">{r.store_display_name}</p>
                <Select value={r.stage} onValueChange={(v) => updateStage(r.id, v)}>
                  <SelectTrigger data-testid={`funding-stage-select-${r.id}`} title="Where this funding application is at — Applied through Funded or Declined." className="w-56 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-igc-muted text-xs mb-3">Requested ${r.amount_requested.toLocaleString()} — Updated {formatET(r.updated_at, "MMM d, yyyy h:mm a")}</p>
              <div className="flex items-center gap-2 mb-3">
                <Input data-testid={`funding-amount-input-${r.id}`} type="number" placeholder="Amount funded ($)" title="The final dollar amount actually funded to this client, once approved." defaultValue={r.amount_funded || ""} onChange={(e) => setAmountDraft({ ...amountDraft, [r.id]: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-48" />
                <Button data-testid={`funding-amount-save-${r.id}`} onClick={() => saveAmount(r.id, r.amount_funded)} title="Saves the funded amount you just typed." className="bg-igc-gold hover:bg-igc-goldHover text-black">Save Amount</Button>
              </div>
              <Textarea
                data-testid={`funding-notes-input-${r.id}`}
                defaultValue={r.admin_notes}
                onChange={(e) => setNotesDraft({ ...notesDraft, [r.id]: e.target.value })}
                placeholder="Internal notes (admin-only)..."
                title="Private notes about this application, visible only to admins."
                className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream mb-3 min-h-20"
              />
              <Button data-testid={`funding-notes-save-${r.id}`} onClick={() => saveNotes(r.id, r.admin_notes)} title="Saves the note you just typed." className="bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">
                Save Notes
              </Button>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
